/**
 * Тонкая обёртка над fetch для /api.
 *
 * Аутентификация — httpOnly-куки, выставленные POST /api/auth/session: токена в
 * JS нет вообще, поэтому здесь нет ни хранилища токенов, ни логики их
 * обновления — протухший access бэкенд перевыпускает сам по refresh-куке.
 *
 * X-Requested-With обязателен: бэкенд требует его (или Sec-Fetch-Site) на
 * небезопасных методах как защиту от CSRF.
 */

export class ApiError extends Error {
  readonly status: number
  readonly detail: unknown

  constructor(status: number, detail: unknown, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined | Array<string | number>
>

function buildUrl(path: string, params?: QueryParams): string {
  const url = new URL(path, window.location.origin)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, String(item))
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.pathname + url.search
}

async function readDetail(response: Response): Promise<unknown> {
  try {
    const body: unknown = await response.json()
    return body && typeof body === 'object' && 'detail' in body ? body.detail : body
  } catch {
    // Пустое тело или не-JSON (например, 502 от прокси) — деталей нет.
    return null
  }
}

async function parseError(response: Response): Promise<ApiError> {
  const detail = await readDetail(response)
  const message =
    typeof detail === 'string' ? detail : `Запрос завершился с кодом ${response.status}`
  return new ApiError(response.status, detail, message)
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  params?: QueryParams
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', params, body, signal } = options
  const headers: Record<string, string> = { 'X-Requested-With': 'askona-web' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const response = await fetch(buildUrl(path, params), {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, params?: QueryParams, signal?: AbortSignal) =>
    request<T>(path, { params, signal }),
  post: <T>(path: string, body?: unknown, params?: QueryParams) =>
    request<T>(path, { method: 'POST', body, params }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
